import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Form as AntForm } from 'antd';
import { EnvelopeIcon, FileAltIcon } from 'assets/icons';
import { DataContainer } from 'components/DataContainer';
import { Dialog, useDisclosure } from 'components/Dialog';
import {
  getEmailFromGarageData,
  sendEmailFromGarageRequestSaga,
  setEmailFromGarageData,
} from 'domains/email/Email.store';
import { Flex, Icon, Input, MarginBox, Space, Text, TextArea } from 'UI';
import { getData } from 'utils';
import { AttachmentPreview } from './SendEmailDialog.styled';

interface SendEmailDialogProps {
  title: string;
  triggerElement: {
    element: React.ReactNode;
    disabled: boolean;
  };
  additionalOptions?: React.ReactNode;
}

type FormValues = { email: string };

interface DialogContentProps {
  recipient: string;
  subject: string;
  body: string;
  attachments?: Array<{ fileName: string; fileBase64: string }>;
  additionalOptions?: React.ReactNode;
  onRecipientChange: (value: string) => void;
  onSubjectChange: (value: string) => void;
  onBodyChange: (value: string) => void;
  onOpenAttachment: (index: number) => Promise<void>;
  onEmailValidChange: (isValid: boolean) => void;
}

const DialogContent = ({
  recipient,
  subject,
  body,
  attachments,
  additionalOptions,
  onRecipientChange,
  onSubjectChange,
  onBodyChange,
  onOpenAttachment,
  onEmailValidChange,
}: DialogContentProps) => {
  const { t } = useTranslation();
  const [form] = AntForm.useForm<FormValues>();

  useEffect(() => {
    form.setFieldsValue({ email: recipient });
    form
      .validateFields(['email'])
      .then(() => onEmailValidChange(true))
      .catch(() => onEmailValidChange(false));
  }, [form, recipient, onEmailValidChange]);

  return (
    <Flex direction={'column'} minWidth={900}>
      <Space direction={'vertical'} size={15}>
        {additionalOptions}

        <AntForm<FormValues>
          form={form}
          layout="vertical"
          validateMessages={{
            required: t('common.email', 'E-mail address'),
            types: {
              email: t('common.email.validation', 'Please enter valid e-mail address.'),
            },
          }}
          initialValues={{ email: recipient ?? '' }}
        >
          <AntForm.Item name="email" rules={[{ required: true, type: 'email' }]}>
            <Input
              placeholder={t('common.email.address.placeholder', `Client's email`)}
              bordered
              value={recipient}
              onChange={onRecipientChange}
            />
          </AntForm.Item>
        </AntForm>
        <Input placeholder={t('common.email.subject', 'Subject')} bordered value={subject} onChange={onSubjectChange} />
        <MarginBox mt={15} />
        <TextArea size={'large'} rows={10} allowEnterNextLine initialValue={body} onChange={onBodyChange} />
        <Flex>
          <DataContainer data={attachments} NotFound={() => <></>}>
            {attachments?.map((attachment, index) => {
              const attachmentKey = `${attachment.fileName}_${attachment.fileBase64}`;
              return (
                <AttachmentPreview key={attachmentKey}>
                  <Flex align={'center'} minHeight={35}>
                    <Space direction={'horizontal'} size={8}>
                      <MarginBox mt={5}>
                        <Icon IconComponent={FileAltIcon} size={18} color={'red'} />
                      </MarginBox>
                      <Text type="lg" bold>
                        {t('common.file_upload.file', 'File:')}
                      </Text>
                      <Text type="lg" hoverUnderLine cursor={'pointer'} onClick={() => onOpenAttachment(index)}>
                        {attachment.fileName}
                      </Text>
                    </Space>
                  </Flex>
                </AttachmentPreview>
              );
            })}
          </DataContainer>
        </Flex>
      </Space>
    </Flex>
  );
};

//todo handle multiple recipients and attachments if ever described by business
const SendEmailDialog = ({ title, triggerElement, additionalOptions }: SendEmailDialogProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const emailData = useSelector(getEmailFromGarageData);
  const attachments = getData(emailData.attachments);
  const disclosure = useDisclosure();
  const { onOpen } = disclosure;
  const recipient = emailData.recipients[0] ?? '';
  const [isEmailValid, setIsEmailValid] = useState(false);

  const updateEmailFromGarageData = (payload: Partial<typeof emailData>) => {
    dispatch(setEmailFromGarageData({ ...emailData, ...payload }));
  };

  useEffect(() => {
    if (!disclosure.isOpen) {
      setIsEmailValid(false);
    }
  }, [disclosure.isOpen]);

  const onOpenAttachment = async (index: number) => {
    if (attachments?.[index]) {
      const data = await fetch(`data:application/pdf;base64,${attachments[index].fileBase64}`);
      const blob = await data.blob();
      if (blob) {
        const url = URL.createObjectURL(blob);
        if (url) {
          window.open(url);
        }
      }
    }
  };

  return (
    <>
      <div onClick={triggerElement.disabled ? undefined : onOpen}>{triggerElement.element}</div>
      <Dialog
        width={1000}
        disclosure={disclosure}
        title={title}
        icon={EnvelopeIcon}
        handleConfirm={() => dispatch(sendEmailFromGarageRequestSaga())}
        isConfirmButtonDisabled={!isEmailValid}
        buttonsText={{ confirm: t('common.action.send', 'Send') }}
        content={
          <DialogContent
            recipient={recipient}
            subject={emailData.subject}
            body={emailData.body}
            attachments={attachments}
            additionalOptions={additionalOptions}
            onRecipientChange={(value) => updateEmailFromGarageData({ recipients: [value] })}
            onSubjectChange={(value) => updateEmailFromGarageData({ subject: value })}
            onBodyChange={(value) => updateEmailFromGarageData({ body: value })}
            onOpenAttachment={onOpenAttachment}
            onEmailValidChange={setIsEmailValid}
          />
        }
      />
    </>
  );
};

export default SendEmailDialog;
